import { useState, useEffect } from 'react'
import { isMobile } from 'react-device-detect'

export default function useKeyboardHeight() {
    const [keyboardHeight, setKeyboardHeight] = useState(0)
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)

    useEffect(() => {
        if (!isMobile) return

        let lastHeight = window.innerHeight

        // Use visualViewport API if available (better for mobile browsers)
        if ('visualViewport' in window) {
            const handleViewportChange = () => {
                const currentHeight = window.visualViewport.height
                const windowHeight = window.innerHeight
                const keyboardHeight = windowHeight - currentHeight

                setKeyboardHeight(keyboardHeight)
                setIsKeyboardVisible(keyboardHeight > 100) // Threshold to avoid false positives
            }

            window.visualViewport.addEventListener('resize', handleViewportChange)
            window.visualViewport.addEventListener('scroll', handleViewportChange)

            return () => {
                window.visualViewport.removeEventListener('resize', handleViewportChange)
                window.visualViewport.removeEventListener('scroll', handleViewportChange)
            }
        } else {
            // Fallback for older browsers
            const handleResize = () => {
                const currentHeight = window.innerHeight
                const heightDiff = lastHeight - currentHeight

                // Only consider it keyboard if height decreased significantly
                if (heightDiff > 100) {
                    setKeyboardHeight(heightDiff)
                    setIsKeyboardVisible(true)
                } else if (currentHeight >= lastHeight - 50) {
                    // Allow some tolerance when keyboard closes
                    setKeyboardHeight(0)
                    setIsKeyboardVisible(false)
                }

                lastHeight = currentHeight
            }

            window.addEventListener('resize', handleResize)

            // Also listen for focus events on inputs
            const handleFocus = e => {
                if (
                    e.target.tagName === 'INPUT' ||
                    e.target.tagName === 'TEXTAREA' ||
                    e.target.contentEditable === 'true'
                ) {
                    // Delay to allow keyboard to open
                    setTimeout(handleResize, 300)
                }
            }

            const handleBlur = () => {
                // Delay to allow keyboard to close
                setTimeout(handleResize, 300)
            }

            document.addEventListener('focusin', handleFocus)
            document.addEventListener('focusout', handleBlur)

            return () => {
                window.removeEventListener('resize', handleResize)
                document.removeEventListener('focusin', handleFocus)
                document.removeEventListener('focusout', handleBlur)
            }
        }
    }, [])

    return { keyboardHeight, isKeyboardVisible }
}
